"use client";

import { Notification } from "../mocks/notifications.mock";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  FileText,
  Home,
  Info,
  MessageSquare,
  MoreHorizontal,
  Settings,
  X,
} from "lucide-react";
import React, { useState } from 'react';

interface NotificationListProps {
  notifications: Notification[];
  onMarkAsRead: (notification: Notification) => void;
  onDelete: (notification: Notification) => void;
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  onMarkAsRead,
  onDelete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "info":
        return <Info className="h-4 w-4 text-blue-500" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error":
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getNotificationStyle = (type: string) => {
    switch (type) {
      case 'info':
        return "bg-blue-500/10 text-blue-500";
      case 'success':
        return "bg-green-500/10 text-green-500";
      case 'warning':
        return "bg-orange-500/10 text-orange-500";
      case 'error':
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case "documents":
        return <FileText className="h-4 w-4 text-blue-500" />;
      case "properties":
        return <Home className="h-4 w-4 text-green-500" />;
      case "payments":
        return <CreditCard className="h-4 w-4 text-purple-500" />;
      case "requests":
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      case "system":
        return <Settings className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getModuleText = (module: string) => {
    switch (module) {
      case "documents":
        return "Документи";
      case "properties":
        return "Нерухомість";
      case "payments":
        return "Платежі";
      case "requests":
        return "Запити";
      case "system":
        return "Система";
      default:
        return module;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Info className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Сповіщення</h1>
            <p className="text-muted-foreground">
              Перегляд та управління сповіщеннями
            </p>
          </div>
        </div>
        <Button variant="outline">
          <Info className="mr-2 h-4 w-4" />
          Позначити все як прочитане
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Фільтри</CardTitle>
          <CardDescription>Налаштуйте параметри відображення сповіщень</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Info className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Пошук сповіщень..."
                className="w-full pl-9"
                type="search"
              />
            </div>
            <Select
              value={selectedType}
              onValueChange={setSelectedType}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Тип сповіщення" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всі типи</SelectItem>
                <SelectItem value="info">Інформаційні</SelectItem>
                <SelectItem value="success">Успішні</SelectItem>
                <SelectItem value="warning">Попередження</SelectItem>
                <SelectItem value="error">Помилки</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={selectedStatus}
              onValueChange={setSelectedStatus}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всі статуси</SelectItem>
                <SelectItem value="read">Прочитані</SelectItem>
                <SelectItem value="unread">Непрочитані</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline">
              <Info className="mr-2 h-4 w-4" />
              Більше фільтрів
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Всі сповіщення</CardTitle>
              <CardDescription>
                У вас {notifications.filter(n => n.status === 'unread').length} непрочитаних сповіщень
              </CardDescription>
            </div>
            <Button variant="outline">
              <Info className="mr-2 h-4 w-4" />
              Сортувати
            </Button>
          </div>
        </CardHeader>
        <ScrollArea className="h-[calc(100vh-400px)]">
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Тип</TableHead>
                  <TableHead>Заголовок</TableHead>
                  <TableHead>Повідомлення</TableHead>
                  <TableHead>Модуль</TableHead>
                  <TableHead className="w-[150px]">Дата</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow
                    key={notification.id}
                    className={cn(
                      "group",
                      !notification.isRead && "bg-muted/30 hover:bg-muted/50"
                    )}
                  >
                    <TableCell>{getNotificationIcon(notification.type)}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {notification.title}
                        {!notification.isRead && (
                          <Badge
                            variant="secondary"
                            className="bg-primary/10 text-primary text-xs"
                          >
                            Нове
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {notification.message}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getModuleIcon(notification.module)}
                        <span className="text-sm text-muted-foreground">
                          {getModuleText(notification.module)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(notification.createdAt).toLocaleDateString("uk-UA", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!notification.isRead && (
                            <DropdownMenuItem onClick={() => onMarkAsRead(notification)}>
                              Позначити як прочитане
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => onDelete(notification)}
                            className="text-destructive"
                          >
                            Видалити
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </ScrollArea>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <Info className="h-6 w-6 animate-spin" />
        </div>
      )}
    </motion.div>
  );
};

export default NotificationList; 